/**
 * Latest Prices Component
 * Financial-style ticker display showing the latest price for each series
 */

import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { ChartSeries } from '../types/pricing.js';
import { getSeriesColor } from '../utils/series-colors.js';

/**
 * Price information for display
 */
interface PriceInfo {
  name: string;
  price: number;
  change: number;
  changePercent: number;
  color: string;
  isPositive: boolean;
}

@customElement('latest-prices')
export class LatestPrices extends LitElement {
  // Use Light DOM for Tailwind utilities
  protected createRenderRoot() {
    return this;
  }

  @property({ type: Array })
  chartSeries: ChartSeries[] = [];

  @property({ type: String })
  timeframe: string = 'd';

  /**
   * Extract latest price information from chart series
   */
  private getPriceInfo(): PriceInfo[] {
    return this.chartSeries
      .map((series) => {
        if (series.data.length === 0) {
          return null;
        }

        // Get the last two data points
        const latest = series.data[series.data.length - 1];
        const previous = series.data[series.data.length - 2];

        // Calculate change if we have a previous value
        let change = 0;
        let changePercent = 0;

        if (previous && previous.value) {
          change = latest.value - previous.value;
          changePercent = (change / previous.value) * 100;
        }

        return {
          name: series.name,
          price: latest.value,
          change,
          changePercent,
          color: getSeriesColor(series.name),
          isPositive: change >= 0,
        };
      })
      .filter((info): info is PriceInfo => info !== null);
  }

  /**
   * Format price with appropriate decimal places
   */
  private formatPrice(price: number): string {
    return price.toFixed(2);
  }

  /**
   * Format change percentage
   */
  private formatChangePercent(percent: number): string {
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(2)}%`;
  }

  /**
   * Format absolute change value
   */
  private formatChange(change: number): string {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}`;
  }

  /**
   * Render a single price card
   */
  private renderPriceCard(info: PriceInfo) {
    const changeColor = info.isPositive ? 'text-success' : 'text-error';
    const arrowIcon = info.isPositive ? '▲' : '▼';

    return html`
      <div
        class="bg-surface-elevated border border-surface-border p-4 flex flex-col gap-2"
      >
        <!-- Series Name with Color Indicator -->
        <div class="flex items-center gap-2">
          <div
            class="w-3 h-3 rounded-sm"
            style="background-color: ${info.color}"
          ></div>
          <span class="text-text-secondary text-sm font-medium truncate">
            ${info.name}
          </span>
        </div>

        <!-- Current Price -->
        <div class="font-mono text-2xl md:text-3xl text-text-primary font-semibold">
          $${this.formatPrice(info.price)}
        </div>

        <!-- Change Information -->
        <div class="flex items-center gap-2 text-sm">
          <span class="${changeColor} font-mono font-medium">
            ${arrowIcon} ${this.formatChange(info.change)}
          </span>
          <span class="${changeColor} font-mono">
            (${this.formatChangePercent(info.changePercent)})
          </span>
        </div>
      </div>
    `;
  }

  /**
   * Render empty state
   */
  private renderEmpty() {
    return html`
      <div class="text-center py-8 text-text-secondary text-sm">
        Select a series to view pricing information
      </div>
    `;
  }

  render() {
    const priceInfo = this.getPriceInfo();

    if (priceInfo.length === 0) {
      return this.renderEmpty();
    }

    return html`
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6"
      >
        ${priceInfo.map((info) => this.renderPriceCard(info))}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'latest-prices': LatestPrices;
  }
}
